import React, { useRef, useState } from 'react';

import styles from './Autocomplete.module.css';

import { useTranslation } from 'react-i18next';

import Dropdown from '../Dropdown/Dropdown';
import SelectTriangle from '../SelectTriangle/SelectTriangle';

export interface IOption<T extends string | number | boolean | null> {
    id: T
    label: React.ReactNode
}
interface Props<T extends string | number> {
    id?: string
    options: IOption<T>[]
    placeholder?: string
    defaultID?: T | null
    emptyOptionsMessage?: string
    value?: T | null
    style?: React.CSSProperties
    error?: boolean
    onChange: (id: T) => void
    renderInput?: ({ showOptions, label, onClick }: { showOptions: boolean, label: React.ReactNode | undefined, onClick: (e?: React.MouseEvent) => void }) => React.ReactNode

}

const Autocomplete = <T extends string | number>({
    id,
    options,
    placeholder,
    value,
    defaultID = null,
    emptyOptionsMessage,
    style,
    error,
    onChange,
    renderInput
}: Props<T>) => {

    const { t } = useTranslation();

    const [showOptions, setShowOptions] = useState(false);
    const [currentOptionID, setCurrentOptionID] = useState<IOption<T>['id'] | null>(defaultID);

    const selectedOptionID = value !== undefined ? value : currentOptionID;

    const handleChangeOption = (id: IOption<T>['id']) => () => {
        setShowOptions(false);
        if (value === undefined) setCurrentOptionID(id);
        onChange(id);
    };

    const selectRef = useRef<HTMLInputElement | null>(null);

    const label = options.find(o => o.id === selectedOptionID)?.label;

    const onClickInput = () => setShowOptions(x => !x);

    return (
        <div
            id={id}
            className={`${styles.select} `}
            style={{ ...style }}
            ref={selectRef}
        >
            {renderInput?.({ showOptions: showOptions, label: label, onClick: onClickInput }) ??
                <button className={`${styles.selectButton} ${error ? styles.error : ''}`} onClick={() => setShowOptions(val => !val)}>
                    {label ?
                        <div className={styles.label}>{label}</div>
                        :
                        <p className={styles.placeholder}>{placeholder ?? ''}</p>
                    }
                    <SelectTriangle show={showOptions} />
                </button>
            }
            <Dropdown
                show={showOptions}
                elementRef={selectRef}
                handleClickOutside={() => setShowOptions(false)}
            >
                <div className={styles.options}>
                    {options.length === 0 ?
                        <p className={styles.emptyMessage}>{emptyOptionsMessage ?? t('empty_option_list')}</p>
                        :
                        options.map(option =>
                            <div
                                key={option.id}
                                onClick={handleChangeOption(option.id)}
                                className={`${styles.option} ${option.id === selectedOptionID ? styles.selectedOption : ''}`}
                            >
                                {option.label}
                            </div>
                        )}
                </div>
            </Dropdown>
        </div>
    );
};

export default Autocomplete;